<template>
  <div class="ai-message-content">
    <template
      v-for="(block, index) in blocks"
      :key="index"
    >
      <pre
        v-if="block.type === 'code'"
        class="ai-code-block"
      ><code>{{ block.text }}</code></pre>

      <component
        :is="`h${block.level}`"
        v-else-if="block.type === 'heading'"
        class="ai-heading"
      >
        <AiInline :parts="block.parts" />
        <span
          v-if="caretAfter(index)"
          class="ai-caret"
        />
      </component>

      <component
        :is="block.ordered ? 'ol' : 'ul'"
        v-else-if="block.type === 'list'"
        class="ai-list"
      >
        <li
          v-for="(item, i) in block.items"
          :key="i"
        >
          <AiInline :parts="item" />
          <span
            v-if="caretAfter(index) && i === block.items.length - 1"
            class="ai-caret"
          />
        </li>
      </component>

      <!-- Le tableau défile dans son propre cadre : une bulle de chat est
           étroite, et le débordement pousserait la fenêtre entière. -->
      <div
        v-else-if="block.type === 'table'"
        class="ai-table-wrap"
      >
        <table class="ai-table">
          <thead>
            <tr>
              <th
                v-for="(cell, c) in block.header"
                :key="c"
                v-text="cell"
              />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, r) in block.rows"
              :key="r"
            >
              <td
                v-for="(cell, c) in row"
                :key="c"
                v-text="cell"
              />
            </tr>
          </tbody>
        </table>
      </div>

      <p
        v-else
        class="ai-text-block"
      >
        <template
          v-for="(line, i) in block.lines"
          :key="i"
        >
          <br v-if="i > 0">
          <AiInline :parts="line" />
        </template>
        <span
          v-if="caretAfter(index)"
          class="ai-caret"
        />
      </p>
    </template>

    <!-- Le dernier bloc est un tableau, un bloc de code, ou la réponse n'a pas
         encore commencé : le curseur ne peut pas suivre le texte, il se pose
         en dessous. -->
    <span
      v-if="pending && !caretInline"
      class="ai-caret"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { parseBlocks } from '@/utils/luceMarkdown';
import AiInline from './AiInline.vue';

/**
 * Rendu du contenu d'un message.
 *
 * Aucun `v-html` : le texte est interpolé dans des balises choisies ici, donc
 * échappé par Vue. Une réponse contenant du HTML s'affiche telle quelle au lieu
 * d'être exécutée — la question de la sanitisation ne se pose pas.
 *
 * Le Markdown est analysé (gras, code, titres, listes) plutôt qu'affiché brut :
 * les modèles structurent spontanément leurs réponses, et les astérisques et
 * accents graves à l'écran rendaient les réponses pénibles à lire.
 */
const props = defineProps({
  content: { type: String, default: '' },
  pending: { type: Boolean, default: false },
});

const blocks = computed(() => parseBlocks(props.content));

/**
 * Le curseur de frappe suit la dernière lettre écrite.
 *
 * Posé après la liste des blocs, il tomberait sous le paragraphe en cours — un
 * trait qui clignote dans le vide, une ligne trop bas. On l'insère donc dans le
 * dernier bloc quand celui-ci se termine par du texte. Un tableau ou un bloc de
 * code n'ont pas de fin de ligne où l'accrocher : il reste en dessous.
 */
const CARET_INLINE_TYPES = ['paragraph', 'heading', 'list'];

const caretInline = computed(() => {
  const dernier = blocks.value[blocks.value.length - 1];

  return props.pending && !!dernier && CARET_INLINE_TYPES.includes(dernier.type);
});

function caretAfter(index) {
  return caretInline.value && index === blocks.value.length - 1;
}
</script>

<style scoped>
.ai-message-content {
  word-break: break-word;
}

.ai-text-block {
  margin: 0 0 0.6em;
  line-height: 1.55;
}

.ai-message-content > *:last-child {
  margin-bottom: 0;
}

.ai-heading {
  margin: 0.8em 0 0.4em;
  font-size: 1em;
  font-weight: 600;
  line-height: 1.4;
}

.ai-message-content > .ai-heading:first-child {
  margin-top: 0;
}

.ai-list {
  margin: 0 0 0.6em;
  padding-left: 1.35em;
  line-height: 1.55;
}

.ai-list li {
  margin-bottom: 0.2em;
}

.ai-table-wrap {
  margin: 0 0 0.6em;
  overflow-x: auto;
}

.ai-table {
  border-collapse: collapse;
  font-size: 0.92em;
  min-width: 100%;
}

.ai-table th,
.ai-table td {
  border: 1px solid var(--el-border-color-lighter);
  padding: 5px 9px;
  text-align: left;
  vertical-align: top;
  white-space: nowrap;
}

.ai-table th {
  background: var(--el-fill-color-light);
  font-weight: 600;
}

.ai-code-block {
  margin: 0 0 0.6em;
  padding: 12px 14px;
  border-radius: 6px;
  background: var(--el-fill-color-darker, #f4f4f5);
  border: 1px solid var(--el-border-color-lighter);
  overflow-x: auto;
  font-size: 13px;
  line-height: 1.5;
}

.ai-code-block code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  white-space: pre;
}

/* Curseur de frappe pendant la génération. */
.ai-caret {
  display: inline-block;
  margin-left: 2px;
  width: 7px;
  height: 1em;
  vertical-align: text-bottom;
  background: var(--el-color-primary);
  animation: ai-caret-blink 1s steps(2, start) infinite;
}

@keyframes ai-caret-blink {
  to {
    visibility: hidden;
  }
}
</style>
